import {
  checkoutApps,
  getAppsPaymentRequirement,
  type OwnBillingOutcome,
  type PaymentRequirement,
} from '@/services/appsApi'

/**
 * Zahlungspflicht nach dem Abgleich plus Ergebnis einer dabei ausgelösten
 * Buchung: Im Organisations-Scope startet das Backend auch die Abos der
 * Unternehmen mit eigener Abrechnung oder überspringt sie mit Grund
 * (Spec 2026-10-21). Ohne Buchung sind beide Listen leer.
 */
export interface SettledPaymentRequirement extends PaymentRequirement {
  outcome: OwnBillingOutcome
}

/** Unternehmen nur übergeben, wenn gesetzt – Aufrufe ohne bleiben wie bisher. */
function companyArgs (idCompany?: number | null): [] | [number] {
  return idCompany ? [idCompany] : []
}

function noOutcome (): OwnBillingOutcome {
  return { startedCompanies: [], skipped: [] }
}

async function book (checkoutSessionId: string | undefined, company: [] | [number]): Promise<OwnBillingOutcome> {
  const result = await checkoutApps(checkoutSessionId, ...company)
  return {
    startedCompanies: result?.startedCompanies ?? [],
    skipped: result?.skipped ?? [],
  }
}

/**
 * Liest die Zahlungsmethoden-Pflicht und legt fehlende App-Abos an, sobald
 * eine Zahlungsmethode hinterlegt ist (auch nach der PayPal-Rückkehr). Das
 * Ergebnis sagt dem Aufrufer, ob der Pflicht-Dialog noch nötig ist, und
 * welche Unternehmen die Buchung gestartet oder übersprungen hat.
 *
 * `checkoutSessionId`: Rückkehr aus dem Setup-Checkout (Query-Parameter
 * `checkout_session`). Das Backend übernimmt dann zuerst die dort
 * hinterlegte Zahlungsmethode und legt die Abos an.
 * `idCompany`: Scope eines Unternehmens mit eigener Abrechnung
 * (Spec 2026-10-21); ohne gilt die Organisation.
 */
export async function settlePaymentRequirement (checkoutSessionId?: string, idCompany?: number | null): Promise<SettledPaymentRequirement> {
  const company = companyArgs(idCompany)
  if (checkoutSessionId) {
    const outcome = await book(checkoutSessionId, company)
    return { ...(await getAppsPaymentRequirement(...company)), outcome }
  }
  const requirement = await getAppsPaymentRequirement(...company)
  if (!requirement.activationPending) {
    return { ...requirement, outcome: noOutcome() }
  }
  const outcome = await book(undefined, company)
  return { ...(await getAppsPaymentRequirement(...company)), outcome }
}

/** Liest die Session-ID der Rückkehr aus dem Setup-Checkout. */
export function checkoutSessionFromQuery (value: unknown): string {
  return typeof value === 'string' && value.startsWith('cs_') ? value : ''
}

/**
 * Unternehmen der Rückkehr aus dem Setup-Checkout der Benutzerverwaltung
 * (/organisation?bereich=benutzer&ansicht=zugang&unternehmen=<id>&checkout_session=…,
 * Spec 2026-10-22). null: organisationsweite Rückkehr wie bisher.
 */
export function checkoutCompanyFromQuery (query: Readonly<Record<string, unknown>>): number | null {
  if (query.bereich !== 'benutzer') {
    return null
  }
  const id = Number(query.unternehmen)
  return Number.isInteger(id) && id > 0 ? id : null
}
